import React, { Component } from 'react';
import classNames from 'classnames';
import { map, get, filter, find, groupBy } from 'lodash';
import PropTypes from 'prop-types';
import { getContext } from '@fiverr-private/fiverr_context';
import sendMixPanelEvent from '../mixpanel';
import { VIEWED_WALKTHROUGH_STEP, DISMISSED_WALKTHROUGH } from '../mixpanel/events';
import 'driver.js/src/driver.scss';
import './index.scss';

const DRIVER_STEPS_PROGRESS_DOTS_CLASSNAME = 'driver-steps-progress-dots';
const DRIVER_PAGE_OVERLAY_ID = 'driver-page-overlay';
const MINIMUM_TOP_OFFSET = 20;

export class Walkthrough extends Component {
    constructor(props) {
        super(props);

        this.state = {
            rendered: null
        };

        this.showSteps = this.showSteps.bind(this);
        this.getStepsDotsTemplate = this.getStepsDotsTemplate.bind(this);
        this.startTour = this.startTour.bind(this);
        this.isElementVisited = this.isElementVisited.bind(this);
        this.clearOverlay = this.clearOverlay.bind(this);
        this.createOverlay = this.createOverlay.bind(this);
        this.getWalkthroughStorageKey = this.getWalkthroughStorageKey.bind(this);
        this.getOverlayClassName = this.getOverlayClassName.bind(this);
        this.markAllSteps = this.markAllSteps.bind(this);
        this.validateTourNameForMixPanelEvent = this.validateTourNameForMixPanelEvent.bind(this);
        this.reportToMixPanelEvent = this.reportToMixPanelEvent.bind(this);
        this.mapDriverStepToUserStep = this.mapDriverStepToUserStep.bind(this);
        this.onStepChangeStarted = this.onStepChangeStarted.bind(this);
        this.onStepChangeEnded = this.onStepChangeEnded.bind(this);
    }

    componentDidMount() {
        const { locale } = getContext();

        const {
            dismissable,
            onComplete,
            children,
            steps,
            nextBtnText,
            prevBtnText,
            doneBtnText,
            noPadding
        } = this.props;

        const Driver = require('driver.js');
        this.validateTourNameForMixPanelEvent();
        this.driver = new Driver({
            animate: false,
            closeBtnText: ' ',
            nextBtnText: nextBtnText || i18n.t('walkthrough.continue', { $scope: locale }),
            prevBtnText: prevBtnText || i18n.t('walkthrough.back', { $scope: locale }),
            doneBtnText: doneBtnText || i18n.t('walkthrough.done', { $scope: locale }),
            allowClose: false,
            scrollIntoViewOptions: { block: 'end' },
            onHighlightStarted: () => {
                setTimeout(() => {
                    this.onStepChangeStarted();
                });
            },
            onHighlighted: () => {
                setTimeout(() => {
                    this.onStepChangeEnded();
                });
            },
            onReset: (DismissedStep) => {
                onComplete && onComplete();
                dismissable && this.markAllSteps(DismissedStep);
                this.clearOverlay();
            },
            padding: noPadding ? 0 : 10
        });

        const { startTour } = this;
        if (!steps.length) { return; }

        const rendered = children({
            startTour
        });

        // eslint-disable-next-line react/no-did-mount-set-state
        this.setState({
            rendered
        });

        !rendered && this.startTour();
    }

    componentDidUpdate(prevProps) {
        if (this.props.steps !== prevProps.steps) {
            if (!this.props.steps.length) { return; }

            const rendered = this.props.children({
                startTour: this.startTour
            });

            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                rendered
            });

            !rendered && this.startTour();
        }
    }

    componentWillUnmount() {
        this.driver = null;
        if (this.popoverItem && this.popoverItem.parentNode) {
            this.popoverItem.parentNode.removeChild(this.popoverItem);
        }
    }

    get popoverItem() {
        const { className } = this.props;
        return document.querySelector(`#driver-popover-item.${className}`);
    }

    validateTourNameForMixPanelEvent() {
        const { name, className } = this.props;

        // Since this package is injected in multiple envs by src we can't be sure that
        // the bundler out there is aligned with this known standard that webpack and other
        // libraries set. But we're sure that most of them are aligned with
        // this but yet we don't want to break things so we wrapping this with try/catch block.
        try {
            if (process.env.NODE_ENV !== 'production') {
                if (!name) {
                    // eslint-disable-next-line no-console
                    console.warn(
                        `Walkthrough Component: Supply "name" prop in order to receive proper MixPanel event name (Sending: ${className})`
                    );
                }
            }
        } catch (e) {
            //
        }
    }

    startTour() {
        const { steps } = this.props;
        const { driver } = this;

        if (driver.isActivated) {
            // eslint-disable-next-line no-console
            console.error('WalkThrough is already in progress.', driver);
            return;
        }

        this.showSteps(steps);
    }

    onStepChangeStarted() {
        const { driver } = this;
        const { className, dismissable, withAnimation } = this.props;
        const steps = driver.getSteps();
        const stepIndex = driver.currentStep;
        const step = steps[stepIndex];

        const popoverClassName = get(step, 'popover.options.className') || '';
        const popoverRootElement = document.querySelector('#driver-popover-item');
        popoverRootElement.className = classNames(
            className,
            'fiverr-walkthrough-popover',
            `steps-${steps.length}`,
            {
                dismissable,
                'with-animation': withAnimation
            },
            popoverClassName
        );

        const progressDotsElement = document.querySelector(`.${className} .${DRIVER_STEPS_PROGRESS_DOTS_CLASSNAME}`);
        if (progressDotsElement && progressDotsElement.parentNode) {
            progressDotsElement.parentNode.removeChild(progressDotsElement);
        }

        const element = document.createElement('div');
        element.innerHTML = this.getStepsDotsTemplate(stepIndex, steps.length);
        popoverRootElement.appendChild(element);
        this.refreshDriverPositionCalculations();
    }

    onStepChangeEnded() {
        const { driver } = this;
        const { onChange } = this.props;
        const steps = driver.getSteps();
        const stepIndex = driver.currentStep;
        const step = steps[stepIndex];

        const visited = localStorage.getItem(this.getWalkthroughStorageKey()) || '';
        localStorage.setItem(this.getWalkthroughStorageKey(), `${visited},${step.options.element}`);

        this.createOverlay();
        onChange && onChange({ stepIndex, step: steps[stepIndex] });
        this.reportToMixPanelEvent(VIEWED_WALKTHROUGH_STEP, step);
        this.refreshDriverPositionCalculations(10);
    }

    refreshDriverPositionCalculations(timeout = 0) {
        setTimeout(() => {

            // trigger a forced refresh and recalculate the highlight position.
            window.dispatchEvent(new Event('resize'));
            this.fixPopoverPosition(this.popoverItem);
        }, timeout);
    }

    fixPopoverPosition(element) {
        if (!element) { return; }

        const rect = element.getBoundingClientRect();

        if (rect.top < MINIMUM_TOP_OFFSET || (rect.top + rect.height + MINIMUM_TOP_OFFSET) > (window.innerHeight)) {
            element.scrollIntoView(true);
            window.scrollBy(0, -20);
        }
    }

    mapDriverStepToUserStep(driverStep) {
        const { steps } = this.props;
        const userStep = find(steps, (s) => s.element === driverStep.options.element);

        return userStep;
    }

    reportToMixPanelEvent(eventName, driverStep, propNamesPrefix = '') {
        const { name, className } = this.props;
        const userStep = this.mapDriverStepToUserStep(driverStep);

        try {
            if (process.env.NODE_ENV !== 'production') {
                if (!userStep.name) {
                    // eslint-disable-next-line no-console
                    console.warn(
                        `Walkthrough Component: Supply "name" prop per step level in order to receive proper MixPanel event name (Sending: ${userStep.element})`
                    );
                }
            }
        } catch (e) {
            //
        }

        sendMixPanelEvent({
            eventName,
            eventData: {
                [`${propNamesPrefix}Walkthrough Name`]: name || className,
                [`${propNamesPrefix}Step Name`]: userStep.name || userStep.element
            }
        });
    }

    getWalkthroughStorageKey() {
        const { className } = this.props;
        return `${className}:walkthrough`;
    }

    markAllSteps(DismissedStep) {
        const { steps } = this.props;
        this.reportToMixPanelEvent(DISMISSED_WALKTHROUGH, DismissedStep, 'Dismissed ');
        const storageValue = steps.map(({ element }) => element).join(',');
        localStorage.setItem(this.getWalkthroughStorageKey(), storageValue);
    }

    createOverlay() {
        const overlay = document.querySelector(`.${this.getOverlayClassName()} #${DRIVER_PAGE_OVERLAY_ID}`);
        if (!overlay) {
            const element = document.createElement('div');
            element.id = DRIVER_PAGE_OVERLAY_ID;
            element.className = this.getOverlayClassName();
            element.style = 'opacity: 0; position: fixed; left: 0px; top: 0px; bottom: 0px; right: 0px;';
            document.body.appendChild(element);
        }
    }

    clearOverlay() {
        const overlay = document.querySelector(`.${this.getOverlayClassName()}`);
        if (overlay.parentNode) {
            overlay.parentNode.removeChild(overlay);
        }
    }

    isElementVisited(element) {
        const visited = localStorage.getItem(this.getWalkthroughStorageKey()) || '';
        return visited.indexOf(element) > -1;
    }

    getOverlayClassName() {
        const { className } = this.props;
        return `${className}-overlay`;
    }

    getStepsDotsTemplate(currentStep, stepsCount) {
        if (stepsCount <= 1) { return ''; }

        const dotsTemplate =
            [...new Array(stepsCount)]
                .map((e, stepIndex) =>
                    `<span class="dot ${stepIndex === currentStep ? 'active' : ''}"></span>`
                ).join('');

        return (`
            <div class="${DRIVER_STEPS_PROGRESS_DOTS_CLASSNAME}">
                ${dotsTemplate}
            </div>
        `);
    }

    showSteps(steps) {
        const { onStarted, onAlreadySeen } = this.props;
        const { driver } = this;
        steps = filter(
            steps,
            (s) => s && (s.popover.force || !this.isElementVisited(s.element))
        ).map((s) => ({
            ...s,
            popover: {
                title: ' ',
                ...s.popover
            }
        }));

        if (!steps.length) {
            onAlreadySeen && onAlreadySeen();
            return;
        }

        clearInterval(this.intervalChecker);
        clearTimeout(this.timeoutChecker);

        this.timeoutChecker = setTimeout(() => {
            try {
                if (process.env.NODE_ENV !== 'production') {
                    const elements = map(steps, (step) => step.element);
                    const elementsGroup = groupBy(elements, (element) => !!document.querySelector(element));

                    // If some elements is false which means not yet exist in the DOM
                    if (elementsGroup.false) {
                        // eslint-disable-next-line no-console
                        console.error(
                            `Walkthrough Component: Timeout while waiting for the elements: [${elementsGroup.false.join(',')}]`
                        );
                    }
                }
            } catch (e) {
                //
            }
        }, 5000);

        this.intervalChecker = setInterval(() => {
            const readyElements = map(steps, (s) => s.element).filter((e) => document.querySelector(e));
            if (readyElements.length === steps.length) {
                clearInterval(this.intervalChecker);
                clearTimeout(this.timeoutChecker);
                driver.defineSteps(steps);
                driver.start();
                onStarted && onStarted();
            }
        }, 500);
    }

    render() {
        return this.state.rendered;
    }
}

Walkthrough.propTypes = {
    children: PropTypes.func,
    name: PropTypes.string,
    onStarted: PropTypes.func,
    onComplete: PropTypes.func,
    onAlreadySeen: PropTypes.func,
    withAnimation: PropTypes.bool,
    onChange: PropTypes.func,
    noPadding: PropTypes.bool,
    className: PropTypes.string.isRequired,
    dismissable: PropTypes.bool.isRequired,
    nextBtnText: PropTypes.string,
    prevBtnText: PropTypes.string,
    doneBtnText: PropTypes.string,
    steps: PropTypes.arrayOf(PropTypes.shape({
        element: PropTypes.string.isRequired,
        name: PropTypes.string,
        popover: PropTypes.shape({
            description: PropTypes.string.isRequired,
            force: PropTypes.bool,
            className: PropTypes.string,
            position: PropTypes.oneOf([
                'left',
                'left-center',
                'left-bottom',
                'top',
                'top-center',
                'top-right',
                'right',
                'right-center',
                'right-bottom',
                'bottom',
                'bottom-center',
                'bottom-right'
            ])
        }).isRequired
    })).isRequired
};

Walkthrough.defaultProps = {
    children: () => null,
    dismissable: false,
    withAnimation: false
};

export default Walkthrough;
